import React, { type ReactNode } from 'react';

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'src/shared/components/ui/dialog';
import { cn } from 'src/shared/helpers/utils';
import { PrimaryButton, SecondaryButton } from 'src/shared/components/button/Button';

import styles from './Confirm.module.scss';

export type ControlledConfirmDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    variant: 'alert' | 'confirm';
    title: string;
    description?: ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    onConfirm: () => void;
    isConfirming?: boolean;
    confirmDisabled?: boolean;
};

/**
 * Controlled Radix dialog for alert (single action) or confirm (cancel + confirm), stacked above app modals (e.g. z-index 1300).
 */
export function ControlledConfirmDialog({
    open,
    onOpenChange,
    variant,
    title,
    description,
    confirmLabel,
    cancelLabel,
    onConfirm,
    isConfirming = false,
    confirmDisabled = false,
}: ControlledConfirmDialogProps) {
    const handleConfirm = () => {
        onConfirm();
    };

    const handleCancel = () => {
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent overlayClassName='!z-[1400]' className={cn('!z-[1401] max-w-md gap-4')} data-testid='controlled-confirm-dialog'>
                <DialogHeader>
                    <DialogTitle className={styles.title}>{title}</DialogTitle>
                    {description ? (
                        <DialogDescription className='text-left text-sm text-muted-foreground'>{description}</DialogDescription>
                    ) : null}
                </DialogHeader>
                <DialogFooter className={styles.actions}>
                    {variant === 'confirm' ? (
                        <>
                            <SecondaryButton type='button' onClick={handleCancel} data-testid='confirm-dialog-cancel'>
                                {cancelLabel ?? 'Cancel'}
                            </SecondaryButton>
                            <PrimaryButton
                                type='button'
                                onClick={handleConfirm}
                                disabled={isConfirming || confirmDisabled}
                                data-testid='confirm-dialog-confirm'
                            >
                                {isConfirming ? 'Processing...' : confirmLabel ?? 'Confirm'}
                            </PrimaryButton>
                        </>
                    ) : (
                        <PrimaryButton
                            type='button'
                            onClick={handleConfirm}
                            disabled={isConfirming || confirmDisabled}
                            data-testid='confirm-dialog-confirm'
                        >
                            {isConfirming ? 'Processing...' : confirmLabel ?? 'OK'}
                        </PrimaryButton>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
